import {
  useEffect,
  useRef,
  useState,
} from 'react';

type UseAuthorizationNotesParams = {
  inputId: string;
  onClose: () => void;
};

export default function useAuthorizationNotes({
  inputId,
  onClose,
}: UseAuthorizationNotesParams) {
  const [activeNotesOption, setActiveNotesOption] = useState<string | null>(null);
  const activeNotesRef = useRef<HTMLDivElement | null>(null);
  const suppressNextOpen = useRef(false);
  const hideNotesTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (hideNotesTimeout.current) clearTimeout(hideNotesTimeout.current);
  }, []);

  function cancelHideNotes() {
    if (hideNotesTimeout.current) {
      clearTimeout(hideNotesTimeout.current);
      hideNotesTimeout.current = null;
    }
  }

  function scheduleHideNotes(optionKey: string) {
    cancelHideNotes();
    hideNotesTimeout.current = setTimeout(() => {
      setActiveNotesOption((current) => (current === optionKey ? null : current));
      hideNotesTimeout.current = null;
    }, 100);
  }

  function activateNotes(optionKey: string, hasNotes: boolean) {
    cancelHideNotes();
    setActiveNotesOption(hasNotes ? optionKey : null);
  }

  function resetNotes() {
    cancelHideNotes();
    setActiveNotesOption(null);
  }

  function handleNotesEscape() {
    suppressNextOpen.current = true;
    onClose();
    // Restore focus after the list and its notes portal close.
    setTimeout(() => document.getElementById(inputId)?.focus(), 0);
  }

  return {
    activeNotesOption,
    activeNotesRef,
    suppressNextOpen,
    activateNotes,
    cancelHideNotes,
    scheduleHideNotes,
    resetNotes,
    setActiveNotesOption,
    handleNotesEscape,
  };
}
